import React, { useState, useEffect, useRef } from "react";
import {
  Input,
  Popover,
  PopoverControl,
  PopoverContent,
  Box,
  List,
  ListItem,
  BoxProps,
  InputProps,
} from "../../";
import { defaultRenderItem, ensureHighlightedItemIsVisible } from "./utils";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type InputDropdownListItem<T = any> = {
  value?: T;
  title: string;
  unclickable?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type InputDropdownRenderListItem<T = any> = (props: {
  itemData: InputDropdownListItem<T>;
  isHighlighted: boolean;
}) => React.ReactNode;

export type InputDropdownOnInputChange = (
  value: string,
  reason: {
    onChange?: boolean;
    onHighlighted?: boolean;
  }
) => void;

export type InputDropdownOnInputFocus = (
  event: React.FocusEvent<HTMLInputElement>
) => void;

export type InputDropdownOnItemHighLighted = (
  item: InputDropdownListItem
) => void;

export type InputDropdownOnItemSelected = (
  item: InputDropdownListItem | undefined
) => void;

export type InputDropdownItemHovered = (hovered: boolean) => void;
export type InputDropdownEnterKeyPressed = (
  event: React.KeyboardEvent<HTMLInputElement>
) => void;

export type InputDropdownProps = {
  /** Data list */
  data: InputDropdownListItem[];
  /** Props for input component */
  inputProps?: InputProps;
  /** Props for dropdown container */
  dropdownContainerProps?: BoxProps;
  /** Should auto highlight first item on input change */
  autoHighlightFirstItem?: boolean;
  /** Default input value */
  defaultValue?: string;
  /** Custom render function of list item */
  renderListItem?: InputDropdownRenderListItem;
  /** Event handler for input value */
  onInputChange?: InputDropdownOnInputChange;
  /** Event handler for input focus */
  onInputFocus?: InputDropdownOnInputFocus;
  /** Event handler for input and dropdown blur */
  onBlur?: () => void;
  /** Event handler for when a list item is highlighted */
  onItemHighLighted?: InputDropdownOnItemHighLighted;
  /** Event handler for when a list item is selected */
  onItemSelected?: InputDropdownOnItemSelected;
  onItemHover?: InputDropdownItemHovered;
  /** Event handler for when user key pressed Enter */
  onEnterKeyPressed?: InputDropdownEnterKeyPressed;
  /** On dropdown popup close i.e. would be invoked on blur, on click outside and on item selection  */
  onDropdownClose?: () => void;
};

export const InputDropdown: React.FC<InputDropdownProps> = ({
  data = [],
  inputProps,
  dropdownContainerProps,
  autoHighlightFirstItem = false,
  defaultValue,
  renderListItem = defaultRenderItem,
  onInputChange,
  onItemHighLighted,
  onItemSelected,
  onItemHover,
  onEnterKeyPressed,
  onInputFocus,
  onBlur,
  onDropdownClose,
}) => {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [inputTerm, setInputTerm] = useState("");
  const [isItemMouseover, setIsItemMouseOver] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const highlightedItem = data?.[highlightedIdx];

  function updateHighlightedIndex({
    index,
    changeOnHighlight = true,
  }: {
    index: number;
    changeOnHighlight?: boolean;
  }) {
    setHighlightedIdx(index);
    const highlightedItem = data?.[index];

    if (highlightedItem && !highlightedItem?.unclickable && changeOnHighlight) {
      setInputTerm(highlightedItem.title);
      if (typeof onItemHighLighted === "function") {
        onItemHighLighted(highlightedItem);
      }
      if (typeof onInputChange === "function") {
        onInputChange(highlightedItem.title, {
          onChange: false,
          onHighlighted: true,
        });
      }
    }
  }

  useEffect(() => {
    if (defaultValue) {
      setInputTerm(defaultValue);
    }
  }, [defaultValue]);

  // Refs
  const itemContainerRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLLIElement>(null);

  // Event handlers
  function openPopover() {
    setIsOpen(true);
  }

  function closePopover() {
    if (typeof onDropdownClose === "function") {
      onDropdownClose();
    }
    setIsOpen(false);
  }

  function onListItemClick(itemData: InputDropdownListItem | undefined) {
    if (itemData?.unclickable) {
      return;
    }

    if (itemData) {
      setInputTerm(itemData.title);
    }

    if (typeof onItemSelected === "function") {
      onItemSelected(itemData);
    }

    closePopover();
  }

  function onInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!isOpen) {
      openPopover();
    }

    if (event.key === "ArrowDown") {
      updateHighlightedIndex({
        index: highlightedIdx >= data.length - 1 ? 0 : highlightedIdx + 1,
      });
    } else if (event.key === "ArrowUp") {
      updateHighlightedIndex({
        index:
          highlightedIdx === -1 || highlightedIdx === 0
            ? data.length - 1
            : highlightedIdx - 1,
      });
    } else if (event.key === "Escape") {
      updateHighlightedIndex({ index: -1 });
      closePopover();
    } else if (event.key === "Enter") {
      if (typeof onEnterKeyPressed === "function") {
        onEnterKeyPressed(event);
      }
      if (data && highlightedIdx !== -1) {
        onListItemClick(highlightedItem);
      } else {
        onListItemClick(undefined);
      }
    }
  }

  function handleItemHover(hovered) {
    setIsItemMouseOver(hovered);

    if (typeof onItemHover === "function") {
      onItemHover(hovered);
    }
  }

  useEffect(() => {
    ensureHighlightedItemIsVisible({ itemRef, itemContainerRef });
  }, [highlightedIdx]);

  const showData = data && data.length;

  return (
    <Popover
      isOpen={isOpen}
      fullWidth
      hideArrow
      hideOnClickOutside
      flexGrow={1}
      role="combobox"
      aria-haspopup="listbox"
      zIndex={3}
      outsideClickHandlerProps={{
        onOutsideClick: closePopover,
      }}
    >
      <PopoverControl>
        {({ ref, isOpen }) => {
          const dropdownOpenProps =
            showData && isOpen
              ? {
                  borderRadius: "default",
                  borderBottomRightRadius: 0,
                  borderBottomLeftRadius: 0,
                }
              : {};

          return (
            <Input
              ref={ref}
              onChange={(event) => {
                const inputValue = event.currentTarget.value;
                setInputTerm(inputValue);
                updateHighlightedIndex({
                  index: autoHighlightFirstItem ? 0 : -1,
                  changeOnHighlight: false,
                });
                if (!isOpen) {
                  openPopover();
                }

                if (typeof onInputChange === "function") {
                  onInputChange(inputValue, {
                    onChange: true,
                    onHighlighted: false,
                  });
                }
              }}
              onBlur={() => {
                if (!isItemMouseover) {
                  closePopover();
                  if (typeof onBlur === "function") {
                    onBlur();
                  }
                }
              }}
              value={inputTerm}
              onFocus={(e) => {
                openPopover();
                if (typeof onInputFocus === "function") {
                  onInputFocus(e);
                }
              }}
              onKeyDown={(event) => onInputKeyDown(event)}
              autoComplete="off"
              {...dropdownOpenProps}
              {...inputProps}
            />
          );
        }}
      </PopoverControl>
      <PopoverContent>
        {showData ? (
          <Box
            ref={itemContainerRef}
            border="1px solid"
            borderColor="ui.borderColor"
            borderBottomLeftRadius="5px"
            borderBottomRightRadius="5px"
            backgroundColor="white"
            borderTop="none"
            maxHeight="200px"
            overflow="auto"
            {...dropdownContainerProps}
          >
            <List
              margin={0}
              type="none"
              role="listbox"
              aria-labelledby={inputProps?.id}
            >
              {data.map((itemData, idx) => {
                const highlighted = highlightedIdx === idx;
                const props = {
                  onClick: () => onListItemClick(itemData),
                  role: "option",
                  highlighted,
                  "aria-selected": highlighted,
                  ref: highlighted ? itemRef : null,
                  "data-suggestion-index": idx,
                };

                return (
                  <ListItem
                    mb="0"
                    key={idx}
                    {...props}
                    onFocus={openPopover}
                    onMouseOver={() => handleItemHover(true)}
                    onMouseLeave={() => handleItemHover(false)}
                  >
                    {renderListItem({
                      itemData: itemData,
                      isHighlighted: highlighted,
                    })}
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};

InputDropdown.displayName = "InputDropdown";

export default InputDropdown;
